/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";

import { SvgHandler } from "../../svg-handler/svg-handler";
import close from "../../../../assets/icons/social/close.svg";
import LoaderOverlay from "../loader-overlay";
import { MEMBER_METADATA_UPDATED, SHOW_MEMBER_METADATA_POPUP, MEMBER_METADATA } from "../../../constants";

import "./capture-user-data.m.css";

export const CaptureUserData = ({ member, getSubscriberMetadata, setSubscriberMetadata }) => {
  const isMemberMetadataUpdated = useSelector((state) =>
    get(state, ["memberMetadata", "isMemberMetadataUpdated"], false)
  );
  const showMemberMetadataPopup = useSelector((state) =>
    get(state, ["memberMetadata", "showMemberMetadataPopup"], true)
  );
  const { enableLocalization = false, localizedElements = {} } = useSelector((state) =>
    get(state, ["qt", "config", "pagebuilder-config", "general", "localization"], {})
  );
  const skipPhoneValidation = useSelector((state) =>
    get(state, ["qt", "config", "pagebuilder-config", "general", "skipPhoneValidation"], false)
  );
  const { shippingAddress: localizedShippingAddress = {}, fieldErrors: localizedFieldErrors = {} } = enableLocalization
    ? localizedElements
    : {};
  const {
    heading: localizedHeading = "",
    name: localizedName = "",
    address: localizedAddress = "",
    city: localizedCity = "",
    state: localizedState = "",
    country: localizedCountry = "",
    postCode: localizedPostCode = "",
    dailCode: localizedDailCode = "",
    phoneNumber: localizedPhoneNumber = "",
    email: localizedEmail = "",
    button: localizedButton = "",
  } = localizedShippingAddress;
  const {
    invalidField: localizedInvalidField,
    invalidPhoneNumber: localizedInvalidPhoneNumber,
    invalidMail: localizedInvalidMail,
    invalidDialCode: localizedInvalidDialCode,
  } = localizedFieldErrors;
  const [formData, setFormData] = useState({
    name: member.name,
    address: "",
    email: member.email,
    city: "",
    country: "",
    state: "",
    postCode: "",
    dialCode: "",
    phoneNumber: "",
  });
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();
  const fields = [
    { name: "name", label: localizedName || "Name", type: "text" },
    { name: "address", label: localizedAddress || "Address", type: "text" },
    { name: "city", label: localizedCity || "City", type: "text" },
    { name: "state", label: localizedState || "State", type: "text" },
    { name: "country", label: localizedCountry || "Country", type: "text" },
    { name: "postCode", label: localizedPostCode || "Post Code", type: "text" },
    { name: "dialCode", label: localizedDailCode || "Dial Code", type: "tel" },
    { name: "phoneNumber", label: localizedPhoneNumber || "Phone Number", type: "tel" },
    { name: "email", label: localizedEmail || "Email", type: "email" },
  ];

  const handleValidation = () => {
    const errors = {};
    let formIsValid = true;
    const regexForEmail = RegExp(/^[_a-z0-9-]+(\.[_a-z0-9-]+)*@[a-z0-9-]+(\.[a-z0-9-]+)*(\.[a-z]{2,4})$/);
    const regexForPhone = RegExp(/^[0-9]{10}$/);
    const regexForPhoneBasic = RegExp(/^[0-9]+$/);
    const regexForDialCode = RegExp(/^[+]?[0-9]*$/);
    const fields = ["name", "city", "state", "address", "postCode", "phoneNumber", "country", "dialCode", "email"].filter(
      (field) => !skipPhoneValidation || field !== "phoneNumber"
    );

    fields.forEach((item) => {
      if (!formData[item]) {
        formIsValid = false;
        errors[item] = localizedInvalidField || "Cannot be empty";
      }
    });

    if (typeof formData.email !== "undefined" && !regexForEmail.test(formData.email)) {
      formIsValid = false;
      errors.email = localizedInvalidMail || "Invalid email-ID";
    }
    if (!skipPhoneValidation && typeof formData.phoneNumber !== "undefined" && !regexForPhone.test(formData.phoneNumber)) {
      formIsValid = false;
      errors.phoneNumber = localizedInvalidPhoneNumber || "Invalid phone number";
    }
    if (skipPhoneValidation && typeof formData.phoneNumber !== "undefined") {
      if (!formData.phoneNumber) {
        formIsValid = false;
        errors.phoneNumber = localizedInvalidPhoneNumber || "Invalid phone number";
      } else if (!regexForPhoneBasic.test(formData.phoneNumber)) {
        formIsValid = false;
        errors.phoneNumber = localizedInvalidPhoneNumber || "Phone number must contain only digits";
      }
    }
    if (typeof formData.dialCode !== "undefined" && !regexForDialCode.test(formData.dialCode)) {
      formIsValid = false;
      errors.dialCode = localizedInvalidDialCode || "Invalid Dial code";
    }

    setErrors(errors);
    return formIsValid;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!handleValidation()) {
      return null;
    }

    const { name, address, city, state, country, postCode, phoneNumber, email, dialCode } = formData;

    const subscriberDetails = {
      address: {
        line1: name,
        line2: address,
        city: city,
        state: state,
        country: country,
        pincode: postCode,
      },
      phone_number: phoneNumber,
      other: {
        email: email,
        dialCode: dialCode,
      },
    };

    try {
      if (subscriberDetails) {
        dispatch({
          type: MEMBER_METADATA_UPDATED,
          isMemberMetadataUpdated: !isMemberMetadataUpdated,
        });
        dispatch({
          type: MEMBER_METADATA,
          data: subscriberDetails
        });
        await setSubscriberMetadata(subscriberDetails); //  Keeping as a fallback.
      }
    } catch (err) {
      console.error(err);
    }
  };

  const toggleForm = (e) => {
    dispatch({
      type: SHOW_MEMBER_METADATA_POPUP,
      showMemberMetadataPopup: !showMemberMetadataPopup,
    });
  };

  const handleChange = (e, name) => {
    setFormData({ ...formData, [name]: e.target.value });
  };

  if (!showMemberMetadataPopup || isMemberMetadataUpdated) {
    return null;
  }

  const getStyleName = (field) => (field === "name" || field === "email" || field === "address" ? "custom-field" : "");

  if (isEmpty(formData)) {
    return <LoaderOverlay />;
  }

  return (
    <div styleName="content-wrapper">
      <div styleName="overlay">
        <div styleName="wrapper">
          <div styleName="close-btn" onClick={(e) => toggleForm(e)}>
            <SvgHandler width="24px" height="24px" xlinkHref={close} />
          </div>
          <h5 styleName="shipping-address">{localizedHeading || "Enter Shipping Address"}</h5>
          <form>
            <div styleName="input-grid">
              {fields.map(({ name, label, type }) => {
                return (
                  <div styleName={`field-wrapper ${getStyleName(name)}`} key={name}>
                    {label && (
                      <label styleName="label">
                        {label}
                        <span styleName="required">*</span>
                      </label>
                    )}
                    <input
                      styleName={`input-field  ${errors[name] ? "error-msg" : ""}`}
                      type={type}
                      value={formData[name]}
                      onChange={(e) => handleChange(e, name)}
                      name={name}
                    />
                    <div styleName="show-error-message">{errors[name]}</div>
                  </div>
                );
              })}
            </div>
            <button type="submit" styleName="submit-btn" onClick={handleSubmit}>
              {localizedButton || "Continue"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

CaptureUserData.propTypes = {
  member: PropTypes.object,
  getSubscriberMetadata: PropTypes.func,
  setSubscriberMetadata: PropTypes.func,
};
